import EmptyFetchError, { createHandledPipe } from 'components/Fetch/FetchError'
import { ContentItem, FilterType, RangeFilter } from 'interfaces'
import { STOCK_CONTENT_PROVIDERS_ALL, StockContentProvider } from 'interfaces/Content/StockContentProvider'
import { useState, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { getPhotos } from 'services/content/photos/actions'
import { getArticles } from 'services/content/articles/actions'
import { getVideos } from 'services/content/videos/actions'
import { getGifs } from 'services/content/gifs/actions'
import { getTexts } from 'services/content/texts/actions'
import { fetchStockContent } from 'services/content/stock/actions'
import { SortBy } from 'services/content/util'
import {
  ARTICLE_TYPE,
  GIF_TYPE,
  PHOTO_TYPE,
  STOCK_ALL_FILTER,
  STOCK_GIF_TYPE,
  STOCK_PHOTO_TYPE,
  STOCK_VIDEO_TYPE,
  TEXT_TYPE, VIDEO_TYPE
} from 'shared'
import { StoreThunkDispatch } from 'store/state'
import { Observable } from 'rxjs/Observable'
import useConditionalEffect from 'hooks/useConditionalEffect'

const PAGE_SIZE_DEFAULT = 20

export type ContentFetchParams = {
  sourceId: string
  sourceType: 'feed' | 'stream'
  contentType: FilterType
  timeRange: RangeFilter
  page: number
  sortBy: SortBy
  stockContentProvider: StockContentProvider
}

type CacheValue = {
  page: number
  content: ContentItem[]
  error: any
  hasNextPage: boolean
}

export function useContentFetch(initialParams: ContentFetchParams) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [params, setParams] = useState(initialParams)
  const [isFetching, setIsFetching] = useState(false)
  const [content, setContent] = useState<ContentItem[]>([])
  const [error, setError] = useState<any>(null)
  const [hasNextPage, setHasNextPage] = useState(true)

  const cache = useRef<Record<string, CacheValue>>({})

  const getCacheKey = useCallback(({ sourceId, sourceType, contentType, timeRange, sortBy }: Omit<ContentFetchParams, 'page'>) => {
    return `${sourceId}:${sourceType}:${contentType}:${timeRange}:${sortBy}`
  }, [])

  const fetchContent = useCallback(({ page, ...restParams }: ContentFetchParams) => {
    const { sourceId, sourceType, contentType, timeRange, sortBy, stockContentProvider } = restParams
    const isStock = contentType.indexOf('stock') !== -1
    const isStream = sourceType === 'stream'
    const cacheKey = getCacheKey(restParams)
    cache.current[cacheKey] = cache.current[cacheKey] || { content: [], page: 0, error: null, hasNextPage: true }

    if (cache.current[cacheKey].page === page && cache.current[cacheKey].content.length > 0) {
      setContent(cache.current[cacheKey].content)
      setError(cache.current[cacheKey].error)
      setHasNextPage(cache.current[cacheKey].hasNextPage)
      return
    }

    let observable: any = null

    switch (contentType) {
      case PHOTO_TYPE:
      case STOCK_PHOTO_TYPE: {
        observable = dispatch(getPhotos(sourceId, isStream, page, timeRange, sortBy, isStock, stockContentProvider))
        break
      }

      case VIDEO_TYPE:
      case STOCK_VIDEO_TYPE: {
        observable = dispatch(getVideos(sourceId, isStream, page, timeRange, sortBy, isStock, stockContentProvider))
        break
      }

      case GIF_TYPE:
      case STOCK_GIF_TYPE: {
        observable = dispatch(getGifs(sourceId, isStream, page, timeRange, sortBy, isStock, stockContentProvider))
        break
      }

      case ARTICLE_TYPE: {
        observable = dispatch(getArticles(sourceId, isStream, page, timeRange, sortBy))
        break
      }

      case TEXT_TYPE: {
        observable = dispatch(getTexts(sourceId, isStream, page, timeRange, sortBy))
        break
      }

      case STOCK_ALL_FILTER: {
        observable = dispatch(fetchStockContent(sourceId, page, timeRange, stockContentProvider))
        break
      }

      default: {
        observable = Observable.empty()
      }
    }

    if (observable) {
      setIsFetching(true)
      if (page === 0) {
        setContent([])
      }

      const onError = (e: any) => {
        setError(e)
        setIsFetching(false)
        cache.current[cacheKey].error = e
      }

      createHandledPipe(
        contentType,
        observable.flatMap((data: any) => {
          return Observable.of(data.items)
        }),
        onError
      ).subscribe((items: ContentItem[]) => {
        const hasNextPage = items.length >= PAGE_SIZE_DEFAULT
        const err = items.length === 0 || !hasNextPage ? new EmptyFetchError(contentType) : null
        cache.current[cacheKey] = {
          page,
          content: cache.current[cacheKey].content.concat(items),
          error: err,
          hasNextPage
        }
        setContent(prevContent => page === 0 ? items : [...prevContent, ...items])
        setHasNextPage(hasNextPage)
        setIsFetching(false)
        setError(err)
      }, onError)
    }

  }, [dispatch, getCacheKey])

  useConditionalEffect(([fetching]) => {
    const cacheKey = getCacheKey(params)
    const cached = cache.current[cacheKey]
    if (!cached) {
      setContent([])
      setHasNextPage(true)
      setError(null)
    }

    if (!fetching) {
      fetchContent({ ...params, page: cached ? cached.page : 0 })
    }
  }, [params, fetchContent, getCacheKey], [isFetching])

  const loadNextPage = () => {
    const cacheKey = getCacheKey(params)
    const cached = cache.current[cacheKey]
    const hasNextPage = cached?.hasNextPage
    if (isFetching || (cached && !hasNextPage)) {
      return
    }

    const nextPage = cached ? cached.page + 1 : 0
    fetchContent({ ...params, page: nextPage })
  }

  const updateFetchParams = useCallback((newParams: Partial<ContentFetchParams>) => {
    setParams(prevParams => {
      const filterChanged = newParams.contentType && newParams.contentType !== prevParams.contentType
      const nextStockProvider = newParams.stockContentProvider || STOCK_CONTENT_PROVIDERS_ALL

      return {
        ...prevParams,
        ...newParams,
        // EXPL: reset stock provider if filter is changed
        stockContentProvider: filterChanged ? STOCK_CONTENT_PROVIDERS_ALL : nextStockProvider
      }
    })
  }, [])

  return {
    isFetching,
    content,
    error,
    hasNextPage,
    fetchParams: params,
    setFetchParams: updateFetchParams,
    loadNextPage
  }
}
